import type { AIArchetype } from '../types/gameTypes';

/**
 * ARCHETYPE COMPLIANCE TABLE — the single source of truth for every autonomous bot
 * decision in the game (purchasing, auctions, upgrades/governance votes and trades).
 *
 * Rule: no decision branch may hardcode a number that belongs here. If a persona needs a
 * new behaviour, add it to the table and both the engine and `scripts/verify-archetypes.ts`
 * pick it up automatically.
 */
export interface ArchetypeProfile {
  /** Human-readable persona label (used in logs / verification output). */
  label: string;
  purchase: {
    /** Hard cash floor: `cash - cost` must stay at or above this. AGGRESSIVE = 0 (all-in). */
    cashFloor: number;
    /** Smallest bite worth taking; below this the bot walks away (or auctions). */
    minBite: number;
    /** Safe tranche band for non-majority buys (CONSERVATIVE 25–40, OPPORTUNISTIC 26–35). */
    trancheMin: number;
    trancheMax: number;
    /** Top of the band for low-volatility districts (Transit hubs / brown). */
    lowVolTrancheMax: number;
    /** Buys the full float when affordable (AGGRESSIVE only). */
    targetBuyout: boolean;
    /** Buys 51 % control when affordable. */
    targetMajority: boolean;
    /** Transit + low-tier utilities are high priority for this persona. */
    prefersLowVol: boolean;
    /** Districts this persona is reluctant to overpay for. */
    dislikesPremium: boolean;
    /** An opponent holding this many tiles in the colour triggers a blocking stake. */
    rivalTilesToBlock: number;
    /** Blocking stake band (OPPORTUNISTIC 26–35). */
    blockingMin: number;
    blockingMax: number;
    /** Gambles at auction when it cannot fund the planned tranche. */
    gambleAtAuction: boolean;
  };
  auction: {
    /** Max willingness-to-pay as a multiple of the fair tranche value. */
    ceiling: number;
    /** Escalated ceiling when the win secures/blocks a district monopoly. */
    monopolyCeiling: number;
    /** May draw emergency credit to stay in the bid war (AGGRESSIVE only). */
    borrowToBid: boolean;
    /** Borrow chunks used, smallest first. */
    borrowChunks: number[];
    /** Folds the moment bidding exceeds fair value (CONSERVATIVE only). */
    foldAboveFairValue: boolean;
    /** Bid increment. */
    bidStep: number;
    /** Cash kept aside from bidding (matches the purchase floor). */
    bidReserve: number;
  };
  upgrade: {
    /** Absolute cash reserve required on top of the build cost. */
    fixedReserve: number;
    /** Multiplier on the build cost kept in reserve (CONSERVATIVE = 3). */
    costMultiplier: number;
    /** Weight applied to the high-traffic board position when ranking candidates. */
    trafficWeight: number;
    /** Tie-break: prefer the highest rent (AGGRESSIVE) or the cheapest build (CONSERVATIVE). */
    rankBy: 'RENT' | 'COST' | 'TRAFFIC';
    /** Votes FOR a proposal only when this reserve is satisfied. */
    voteReserve: number;
    voteCostMultiplier: number;
  };
  trade: {
    /** Accept threshold: value received / value given. */
    acceptRatio: number;
    /** Surcharge charged when the deal hands the counterparty a majority on a tile. */
    majoritySurcharge: number;
    /** Denial premium when the deal would hand the counterparty a full colour monopoly. */
    monopolyDenial: number;
    /** Refuses trades that would leave less than this much cash. */
    cashBuffer: number;
  };
  /** Autonomous treasury management (Financial Desk investments). */
  treasury: {
    /** Fund the persona parks idle cash in (null = never auto-invests). */
    tier: 'SAVINGS' | 'REIT' | 'VENTURE' | null;
    /** Cash above this level is excess and gets deposited automatically. */
    idleThreshold: number;
    /** Cash kept liquid after depositing / minimum cash floor before a forced withdrawal. */
    reserveFloor: number;
    /** Cash below this triggers an instant treasury withdrawal. */
    withdrawFloor: number;
    /** Only invests during these market phases (empty = any phase). */
    phases: ('BOOM' | 'STAGNATION' | 'CRISIS')[];
  };
  /** Autonomous insurance behaviour before rolling the dice. */
  insurance: {
    /** How many tiles ahead the bot scans for rival rent exposure (2–12). */
    scanAhead: number;
    /** Buys a policy when the worst upcoming rent exceeds this share of cash. */
    exposurePct: number;
    /** Which policy tier this persona prefers. */
    policy: 'BASIC' | 'STANDARD' | 'VIP';
  };
}


export const ARCHETYPE_PROFILES: Record<AIArchetype, ArchetypeProfile> = {
  // ---- Venture Tycoon: high variance, all-in, buys control at any price ----
  AGGRESSIVE: {
    label: 'Venture Tycoon',
    purchase: {
      cashFloor: 0,
      minBite: 10,
      trancheMin: 26,
      trancheMax: 100,
      lowVolTrancheMax: 100,
      targetBuyout: true,
      targetMajority: true,
      prefersLowVol: false,
      dislikesPremium: false,
      rivalTilesToBlock: 1,
      blockingMin: 51,
      blockingMax: 100,
      gambleAtAuction: true,
    },
    auction: {
      ceiling: 1.35,
      monopolyCeiling: 1.5,
      borrowToBid: true,
      borrowChunks: [250, 500],
      foldAboveFairValue: false,
      bidStep: 100,
      bidReserve: 0,
    },
    upgrade: {
      fixedReserve: 100,
      costMultiplier: 0,
      trafficWeight: 0.5,
      rankBy: 'RENT',
      voteReserve: 100,
      voteCostMultiplier: 0,
    },
    trade: { acceptRatio: 1.05, majoritySurcharge: 0.5, monopolyDenial: 2.0, cashBuffer: 0 },
    // All-in raider keeps every dollar liquid for takeovers — never auto-invests.
    treasury: { tier: null, idleThreshold: 0, reserveFloor: 0, withdrawFloor: 0, phases: [] },
    insurance: { scanAhead: 12, exposurePct: 0.35, policy: 'VIP' },
  },

  // ---- Prudence Banker: $800 hard floor, 0.85× auction cap, never borrows ----
  CONSERVATIVE: {
    label: 'Prudence Banker',
    purchase: {
      cashFloor: 800,
      minBite: 25,
      trancheMin: 25,
      trancheMax: 40,
      lowVolTrancheMax: 40,
      targetBuyout: false,
      targetMajority: false,
      prefersLowVol: true,
      dislikesPremium: true,
      rivalTilesToBlock: 0,
      blockingMin: 25,
      blockingMax: 40,
      gambleAtAuction: false,
    },
    auction: {
      ceiling: 0.85,
      monopolyCeiling: 0.85,
      borrowToBid: false,
      borrowChunks: [],
      foldAboveFairValue: true,
      bidStep: 50,
      bidReserve: 800,
    },
    upgrade: {
      fixedReserve: 0,
      costMultiplier: 3,
      trafficWeight: 0.25,
      rankBy: 'COST',
      voteReserve: 0,
      voteCostMultiplier: 3,
    },
    trade: { acceptRatio: 1.25, majoritySurcharge: 0.45, monopolyDenial: 2.5, cashBuffer: 1000 },
    // Money Market Fund (fixed 3 %/round) as an inflation shield for excess cash.
    treasury: { tier: 'SAVINGS', idleThreshold: 1200, reserveFloor: 1200, withdrawFloor: 500, phases: [] },
    insurance: { scanAhead: 12, exposurePct: 0.35, policy: 'BASIC' },
  },

  // ---- Vulture Raider: patient, blocks monopolies, buys distressed ----
  OPPORTUNISTIC: {
    label: 'Vulture Raider',
    purchase: {
      cashFloor: 250,
      minBite: 26,
      trancheMin: 26,
      trancheMax: 35,
      lowVolTrancheMax: 35,
      targetBuyout: false,
      targetMajority: true,
      prefersLowVol: false,
      dislikesPremium: false,
      rivalTilesToBlock: 2,
      blockingMin: 26,
      blockingMax: 35,
      gambleAtAuction: true,
    },
    auction: {
      ceiling: 1.05,
      monopolyCeiling: 1.4,
      borrowToBid: false,
      borrowChunks: [],
      foldAboveFairValue: false,
      bidStep: 50,
      bidReserve: 250,
    },
    upgrade: {
      fixedReserve: 1000,
      costMultiplier: 0,
      trafficWeight: 1,
      rankBy: 'TRAFFIC',
      voteReserve: 1000,
      voteCostMultiplier: 0,
    },
    trade: { acceptRatio: 1.15, majoritySurcharge: 0.35, monopolyDenial: 2.2, cashBuffer: 0 },
    // Parks surplus cash (> $1,500) in REITs across the whole cycle — yield + appreciation.
    treasury: { tier: 'REIT', idleThreshold: 1500, reserveFloor: 600, withdrawFloor: 400, phases: [] },
    insurance: { scanAhead: 12, exposurePct: 0.35, policy: 'STANDARD' },
  },
};

/** Profile lookup that never throws (unassigned bots behave as OPPORTUNISTIC). */
export const profileOf = (archetype?: AIArchetype): ArchetypeProfile =>
  ARCHETYPE_PROFILES[archetype ?? 'OPPORTUNISTIC'] ?? ARCHETYPE_PROFILES.OPPORTUNISTIC;

export const ARCHETYPES: AIArchetype[] = ['AGGRESSIVE', 'CONSERVATIVE', 'OPPORTUNISTIC'];

/** Cash still available for a purchase without breaking the persona's hard floor. */
export const purchaseBudgetOf = (player: { cash: number; aiArchetype?: AIArchetype }, unitPrice: number): number => {
  const profile = profileOf(player.aiArchetype);
  return Math.max(0, Math.floor((player.cash - profile.purchase.cashFloor) / Math.max(1, unitPrice)));
};
